// src/components/invitations/ImportRecipients.tsx
import React, { useState } from 'react';
import { useCSVReader } from 'react-papaparse';
import SparkButton from '../common/SparkButton';
import SparkHeading from '../common/SparkHeading';
import SparkCard from '../common/SparkCard';
import SparkFooter from '../common/SparkFooter';

interface ImportRecipientsProps {
  onImportComplete: (
    recipients: Array<{
      name: string;
      email?: string;
      phone?: string;
      status?: string;
    }>
  ) => void;
}

const ImportRecipients: React.FC<ImportRecipientsProps> = ({ onImportComplete }) => {
  const { CSVReader } = useCSVReader();
  const [mappedHeaders, setMappedHeaders] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [csvData, setCsvData] = useState<any[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [step, setStep] = useState<'upload' | 'map' | 'review'>('upload');
  const [error, setError] = useState<string | null>(null);

  const handleOnUploadAccepted = (results: any) => {
    setError(null);
    try {
      console.log('CSV Results:', results);
      
      if (results.data && results.data.length > 0) {
        // Extract headers from the first object's keys
        const headers = Object.keys(results.data[0]);
        setCsvHeaders(headers);
        setCsvData(results.data);
        setStep('map');
      } else {
        setError('No data found in CSV file.');
      }
    } catch (err) {
      console.error('Error parsing CSV:', err);
      setError('Failed to parse CSV. Please check file formatting.');
    }
  };

  const handleHeaderMapping = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setMappedHeaders(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMapComplete = () => {
    if (!mappedHeaders.name) {
      setError('Name column is required for mapping.');
      return;
    }
    if (!mappedHeaders.email && !mappedHeaders.phone) {
      setError('Please map either an Email or Phone column.');
      return;
    }
    setError(null);
    setStep('review');
  };

  const validateEmail = (email: string) => /^\S+@\S+\.\S+$/.test(email);
  const validatePhone = (phone: string) => /^[0-9\-+\(\)\s]+$/.test(phone);

  const processRecipients = () => {
    if (!csvData.length) {
      setError('No CSV data found.');
      return;
    }

    const recipients = csvData
      .map(row => {
        const rawName = row[mappedHeaders.name] || '';
        const rawEmail = row[mappedHeaders.email] || '';
        const rawPhone = row[mappedHeaders.phone] || '';
        
        return {
          name: String(rawName).trim(),
          email: String(rawEmail).trim(),
          phone: String(rawPhone).trim(),
          status: 'pending'
        };
      })
      .filter(r => {
        if (!r.name) return false;
        if (!r.email && !r.phone) return false;
        if (r.email && !validateEmail(r.email)) return false;
        if (r.phone && !validatePhone(r.phone)) return false;
        return true;
      });

    onImportComplete(recipients);
    // reset
    setStep('upload');
    setCsvData([]);
    setCsvHeaders([]);
    setMappedHeaders({ name: '', email: '', phone: '' });
  };

  return (
    <div style={{ width: '100%', boxSizing: 'border-box' }}>
      {error && (
        <div className="spark-message spark-message--error">{error}</div>
      )}

      {step === 'upload' && (
        <div style={{ width: '100%', boxSizing: 'border-box' }}>
          <p
            style={{
              fontSize: '1.25rem',
              marginBottom: '1rem',
              color: 'var(--color-text-primary)'
            }}
          >
            Upload a CSV file with your recipients' information.
          </p>
          <CSVReader
            onUploadAccepted={handleOnUploadAccepted}
            config={{ header: true, skipEmptyLines: true }}
          >
            {({ getRootProps, acceptedFile, ProgressBar, getRemoveFileProps }: any) => (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <SparkButton {...getRootProps()} style={{ marginBottom: '1rem' }}>
                  Browse file
                </SparkButton>
                {acceptedFile && (
                  <SparkCard
                    variant="solid"
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      textAlign: 'center',
                      marginBottom: '1rem'
                    }}
                  >
                    <p
                      style={{
                        fontSize: '1.25rem',
                        fontWeight: 'bold',
                        color: '#000',
                        margin: 0
                      }}
                    >
                      {acceptedFile.name}
                    </p>
                  </SparkCard>
                )}
                <div style={{ width: '100%', marginBottom: '1rem' }}>
                  <ProgressBar
                    style={{
                      height: '10px',
                      borderRadius: '4px',
                      backgroundColor: 'var(--color-accent-violet)',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                {acceptedFile && (
                  <SparkButton {...getRemoveFileProps()}>Remove</SparkButton>
                )}
              </div>
            )}
          </CSVReader>
        </div>
      )}

      {step === 'map' && csvHeaders.length > 0 && (
        <div style={{ width: '100%', boxSizing: 'border-box' }}>
          <SparkHeading level="h3">Map CSV Columns</SparkHeading>
          <p
            style={{
              fontSize: '1.25rem',
              marginBottom: '1.5rem',
              color: 'var(--color-text-primary)'
            }}
          >
            Select which columns match the information we need:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Name */}
            <div>
              <label
                htmlFor="name"
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 600
                }}
              >
                Name Column:
              </label>
              <select
                name="name"
                id="name"
                className="spark-input"
                value={mappedHeaders.name}
                onChange={handleHeaderMapping}
              >
                <option value="">-- Select --</option>
                {csvHeaders.map((header: string, i: number) => (
                  <option key={i} value={header}>
                    {header}
                  </option>
                ))}
              </select>
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 600
                }}
              >
                Email Column:
              </label>
              <select
                name="email"
                id="email"
                className="spark-input"
                value={mappedHeaders.email}
                onChange={handleHeaderMapping}
              >
                <option value="">-- Select --</option>
                {csvHeaders.map((header: string, i: number) => (
                  <option key={i} value={header}>
                    {header}
                  </option>
                ))}
              </select>
            </div>

            {/* Phone */}
            <div>
              <label
                htmlFor="phone"
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 600
                }}
              >
                Phone Column:
              </label>
              <select
                name="phone"
                id="phone"
                className="spark-input"
                value={mappedHeaders.phone}
                onChange={handleHeaderMapping}
              >
                <option value="">-- Select --</option>
                {csvHeaders.map((header: string, i: number) => (
                  <option key={i} value={header}>
                    {header}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <SparkFooter style={{ marginTop: '2rem' }}>
            <SparkButton onClick={() => setStep('upload')}>BACK</SparkButton>
            <SparkButton onClick={handleMapComplete}>NEXT</SparkButton>
          </SparkFooter>
        </div>
      )}

      {step === 'review' && (
        <div style={{ width: '100%', boxSizing: 'border-box' }}>
          <SparkHeading level="h3">Review Recipients</SparkHeading>
          <p
            style={{
              fontSize: '1.25rem',
              marginBottom: '1rem',
              color: 'var(--color-text-primary)'
            }}
          >
            Found {csvData.length} rows in the CSV file. Showing first 5 rows below:
          </p>
          <SparkCard
            variant="solid"
            style={{
              marginBottom: '1.5rem',
              maxHeight: '15rem',
              overflowY: 'auto',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            <table style={{ width: '100%', boxSizing: 'border-box' }}>
              <thead style={{ borderBottom: '2px solid black' }}>
                <tr>
                  <th
                    style={{
                      padding: '0.5rem',
                      textAlign: 'left',
                      fontSize: '1.25rem',
                      fontWeight: 900,
                      color: '#000'
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      padding: '0.5rem',
                      textAlign: 'left',
                      fontSize: '1.25rem',
                      fontWeight: 900,
                      color: '#000'
                    }}
                  >
                    Email
                  </th>
                  <th
                    style={{
                      padding: '0.5rem',
                      textAlign: 'left',
                      fontSize: '1.25rem',
                      fontWeight: 900,
                      color: '#000'
                    }}
                  >
                    Phone
                  </th>
                </tr>
              </thead>
              <tbody>
                {csvData.slice(0, 5).map((row: any, i: number) => (
                  <tr key={i} style={{ borderBottom: '1px solid #d1d5db' }}>
                    <td style={{ padding: '0.5rem', fontSize: '1rem', color: '#000' }}>
                      {row[mappedHeaders.name] || ''}
                    </td>
                    <td style={{ padding: '0.5rem', fontSize: '1rem', color: '#000' }}>
                      {row[mappedHeaders.email] || ''}
                    </td>
                    <td style={{ padding: '0.5rem', fontSize: '1rem', color: '#000' }}>
                      {row[mappedHeaders.phone] || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SparkCard>

          <SparkFooter>
            <SparkButton onClick={() => setStep('map')}>BACK</SparkButton>
            <SparkButton onClick={processRecipients}>IMPORT</SparkButton>
          </SparkFooter>
        </div>
      )}
    </div>
  );
};

export default ImportRecipients;